import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, startOfMonth, endOfMonth } from "date-fns";
import {
  BsChevronDown,
  BsChevronRight,
  BsExclamationTriangleFill,
  BsFileEarmarkExcel,
} from "react-icons/bs";
import FGEditModal from "./FGEditModal";
import * as XLSX from "xlsx";

const BASE = "https://eps.elkomprintama.com/";

const toNum = (n) => {
  const v = Number(n);
  return isNaN(v) ? null : v;
};
const fmt = (n) => {
  const v = toNum(n);
  return v === null ? "—" : v.toLocaleString("en-US");
};
const hasError = (r) => toNum(r.finish_qty) === null || toNum(r.qty) === null;

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex flex-col gap-1">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
      {label}
    </p>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
    {sub && <p className="text-xs text-gray-400">{sub}</p>}
  </div>
);

function ProductionReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState([]);
  const [detail, setDetail] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalRecord, setModalRecord] = useState(null);

  const from = format(startOfMonth(new Date(year, month - 1)), "yyyy-MM-dd");
  const to = format(endOfMonth(new Date(year, month - 1)), "yyyy-MM-dd");

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE}get/api/production-report`, {
        params: { from, to },
      });
      setSummary(res.data.summary);
      setDetail(res.data.detail);
      setExpanded({});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const totalJobs = summary.reduce((a, r) => a + Number(r.jobsheet_count), 0);
  const totalQty = summary.reduce((a, r) => a + (toNum(r.total_qty) ?? 0), 0);
  const errorCount = detail.filter(hasError).length;

  const detailFor = (cust_id) => detail.filter((d) => d.cust_id === cust_id);

  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--)
    years.push(y);

  const handleExcel = () => {
    const fileName = `Production_Report_${MONTHS[month - 1]}_${year}.xlsx`;

    // Sheet 1 — Summary
    const summaryRows = summary.map((r) => ({
      Customer: r.customer,
      Jobsheets: Number(r.jobsheet_count),
      "Total Qty": toNum(r.total_qty) ?? 0,
    }));
    summaryRows.push({
      Customer: "TOTAL",
      Jobsheets: totalJobs,
      "Total Qty": totalQty,
    });

    // Sheet 2 — Detail
    const detailRows = detail.map((d) => ({
      "FG ID": d.fg_id,
      Customer: d.nme,
      "Job Name": d.job_name,
      Size: d.size,
      "Part No.": d.pn_cust,
      "PO Number": d.po_num || "",
      "Finish Date": d.finish_date
        ? format(new Date(d.finish_date), "dd MMM yyyy")
        : "",
      "Order Qty": toNum(d.qty) ?? "",
      "Finish Qty": toNum(d.finish_qty) ?? "",
      Operator: d.opr_name || "",
      QC: d.qc_name || "",
    }));

    const wb = XLSX.utils.book_new();

    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    wsSummary["!cols"] = [{ wch: 35 }, { wch: 12 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    const wsDetail = XLSX.utils.json_to_sheet(detailRows);
    wsDetail["!cols"] = [
      { wch: 8 },
      { wch: 30 },
      { wch: 30 },
      { wch: 14 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(wb, wsDetail, "Detail");

    XLSX.writeFile(wb, fileName);
  };

  const toggleRow = (cust_id) =>
    setExpanded((prev) => ({ ...prev, [cust_id]: !prev[cust_id] }));

  return (
    <div
      className="flex flex-col gap-4"
      style={{ maxHeight: "calc(100dvh - 80px)" }}
    >
      {modalRecord && (
        <FGEditModal
          record={modalRecord}
          onClose={() => setModalRecord(null)}
          onSaved={(fg_id, qty, finish_qty) => {
            setDetail((prev) =>
              prev.map((r) =>
                r.fg_id === fg_id ? { ...r, qty, finish_qty } : r,
              ),
            );
            fetchReport();
          }}
        />
      )}

      {/* Page header */}
      <div className="w-full rounded-lg bg-indigo-700 flex-shrink-0">
        <h1 className="block uppercase text-white tracking-wide text-4xl text-center font-bold py-2">
          Production Report
        </h1>
      </div>

      {/* Filter bar */}
      <div className="flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Month
          </label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Year
          </label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => {
            setMonth(now.getMonth() + 1);
            setYear(now.getFullYear());
          }}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition"
        >
          This Month
        </button>
        <p className="text-xs text-gray-400 self-end pb-2">
          {from} → {to}
        </p>
        <button
          onClick={handleExcel}
          disabled={loading || summary.length === 0}
          className="ml-auto flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <BsFileEarmarkExcel size={15} /> Download Excel
        </button>
      </div>

      {/* Stat cards */}
      <div className="flex-shrink-0 grid grid-cols-4 gap-4">
        <StatCard
          label="Customers"
          value={summary.length}
          sub="with completed jobs"
        />
        <StatCard
          label="Total Jobsheets"
          value={fmt(totalJobs)}
          sub="jobs finished"
        />
        <StatCard
          label="Total Qty"
          value={fmt(totalQty)}
          sub="units produced"
        />
        <div
          className={`rounded-xl border shadow-sm px-5 py-4 flex flex-col gap-1 ${errorCount > 0 ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}
        >
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            Data Errors
          </p>
          <p
            className={`text-2xl font-bold ${errorCount > 0 ? "text-red-600" : "text-gray-800"}`}
          >
            {errorCount}
          </p>
          {errorCount > 0 && (
            <p className="text-xs text-red-400">Double-click rows to fix</p>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl border border-gray-200 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            Loading…
          </div>
        ) : summary.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm italic">
            No completed jobs found for this period.
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-indigo-700 text-white uppercase text-xs z-10">
              <tr>
                <th className="px-4 py-3 text-left w-8"></th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-center">Jobsheets</th>
                <th className="px-4 py-3 text-right">Total Qty</th>
                <th className="px-4 py-3 text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row, i) => {
                const rows = detailFor(row.cust_id);
                const isOpen = !!expanded[row.cust_id];
                const rowQty = toNum(row.total_qty) ?? 0;
                const share =
                  totalQty > 0 ? ((rowQty / totalQty) * 100).toFixed(1) : "0.0";
                const custErrors = rows.filter(hasError).length;
                return (
                  <React.Fragment key={row.cust_id}>
                    {/* Summary row */}
                    <tr
                      className={`border-b cursor-pointer select-none transition-colors ${
                        custErrors > 0
                          ? "bg-red-50 hover:bg-red-100"
                          : i % 2 === 0
                            ? "bg-white hover:bg-indigo-50"
                            : "bg-gray-50 hover:bg-indigo-50"
                      }`}
                      onClick={() => toggleRow(row.cust_id)}
                    >
                      <td className="px-4 py-3 text-indigo-400">
                        {isOpen ? (
                          <BsChevronDown size={14} />
                        ) : (
                          <BsChevronRight size={14} />
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        <span>{row.customer}</span>
                        {custErrors > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-red-500">
                            <BsExclamationTriangleFill size={11} />
                            {custErrors} error{custErrors !== 1 ? "s" : ""}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600">
                        {row.jobsheet_count}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-indigo-700">
                        {fmt(rowQty)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-indigo-500 h-1.5 rounded-full"
                              style={{ width: `${share}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 w-10 text-right">
                            {share}%
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded detail rows */}
                    {isOpen &&
                      rows.map((d) => {
                        const err = hasError(d);
                        return (
                          <tr
                            key={d.fg_id}
                            onDoubleClick={() => setModalRecord(d)}
                            title={err ? "Double-click to fix this record" : ""}
                            className={`border-b text-xs cursor-default transition-colors ${
                              err
                                ? "bg-red-50 hover:bg-red-100"
                                : "bg-indigo-50 hover:bg-indigo-100 border-indigo-100"
                            }`}
                          >
                            <td className="px-4 py-2 text-center">
                              {err && (
                                <BsExclamationTriangleFill
                                  size={12}
                                  className="text-red-400"
                                />
                              )}
                            </td>
                            <td
                              className="px-4 py-2 pl-8 text-gray-600"
                              colSpan={1}
                            >
                              <p className="font-medium text-gray-700">
                                {d.job_name}
                              </p>
                              <p className="text-gray-400">
                                {d.size} · {d.pn_cust}
                              </p>
                            </td>
                            <td className="px-4 py-2 text-center text-gray-500">
                              {d.finish_date
                                ? format(new Date(d.finish_date), "dd MMM yyyy")
                                : "—"}
                            </td>
                            <td
                              className={`px-4 py-2 text-right font-semibold ${toNum(d.finish_qty) === null ? "text-red-500" : "text-gray-700"}`}
                            >
                              {fmt(d.finish_qty)}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-400">
                              PO: {d.po_num || "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}

              {/* Totals footer */}
              <tr className="bg-indigo-700 text-white text-sm font-bold">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3">TOTAL</td>
                <td className="px-4 py-3 text-center">{fmt(totalJobs)}</td>
                <td className="px-4 py-3 text-right">{fmt(totalQty)}</td>
                <td className="px-4 py-3 text-right">100%</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ProductionReport;
